import { useEffect, useMemo, useRef, useState } from "react";
import { useTable, useSortBy, useExpanded } from "react-table";

import { useWindowSize } from "@/hooks/SizeHook";

import styles from "./CustomTable.module.scss";

import TableToolbar from "./TableToolbar";
import TableHeader from "./TableHeader";
import TableBody from "./TableBody";
import ResponsiveCardList from "./ResponsiveCardList";

export default function CustomTable({
	columns = [],
	data = [],
	totalItems = 0,
	totalPages = 0,
	toItem = 0,
	fromItem = 0,

	hiddenColumns = [],
	initialPageSize = 10,
	pageSizeOptions = [10, 20, 50, 100],
	initialSortBy = [],
	onRowClick,
	renderSubComponent,
	enableExpander,
	className = "",

	pageIndex: extPageIndex = 1,
	onPageChange,
	pageSize: extPageSize,
	onPageSizeChange,
	sortBy: extSortBy,
	onSortByChange,
	hasFilters = false,

	customHeight,
	customStyle,
	compactHeaders = false,
	compactBody = false,
	useExpandableRows = false,
	renderExpandedContent,
	useRowspan = false,
	rowspanConfig,
	actionButtons,
}) {
	const isArray = (v) => Array.isArray(v);
	const isSortEqual = (a = [], b = []) =>
		isArray(a) && isArray(b) && a.length === b.length && a.every((v, i) => v.id === b[i]?.id && !!v.desc === !!b[i]?.desc);

	const memoizedData = useMemo(() => data, [data]);
	const shouldEnableExpander = enableExpander ?? Boolean(renderSubComponent || renderExpandedContent || useExpandableRows);

	const memoizedColumns = useMemo(() => columns, [columns]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
		setSortBy,
		allColumns,
		state: { sortBy },
		visibleColumns,
	} = useTable(
		{
			columns: memoizedColumns,
			data: memoizedData,
			initialState: {
				hiddenColumns,
				sortBy: Array.isArray(extSortBy) ? extSortBy : Array.isArray(initialSortBy) ? initialSortBy : [],
				expanded: {},
			},
			manualSortBy: true,
		},
		useSortBy,
		useExpanded,
	);

	const [isColumnsMenuVisible, setIsColumnsMenuVisible] = useState(false);
	const columnsMenuRef = useRef(null);

	const headerTableRef = useRef(null);
	const headerContainerRef = useRef(null);
	const bodyContainerRef = useRef(null);
	const isSyncingScrollRef = useRef(false);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (columnsMenuRef.current && !columnsMenuRef.current.contains(event.target)) {
				setIsColumnsMenuVisible(false);
			}
		};
		const handleEsc = (event) => event.key === "Escape" && setIsColumnsMenuVisible(false);

		if (isColumnsMenuVisible) {
			document.addEventListener("mousedown", handleClickOutside);
			document.addEventListener("keydown", handleEsc);
		}
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("keydown", handleEsc);
		};
	}, [isColumnsMenuVisible]);

	useEffect(() => {
		const headerEl = headerContainerRef.current;
		const bodyEl = bodyContainerRef.current;
		if (!headerEl || !bodyEl) return;

		const onBodyScroll = () => {
			if (isSyncingScrollRef.current) return;
			isSyncingScrollRef.current = true;
			headerEl.scrollLeft = bodyEl.scrollLeft;
			isSyncingScrollRef.current = false;
		};
		const onHeaderScroll = () => {
			if (isSyncingScrollRef.current) return;
			isSyncingScrollRef.current = true;
			bodyEl.scrollLeft = headerEl.scrollLeft;
			isSyncingScrollRef.current = false;
		};

		bodyEl.addEventListener("scroll", onBodyScroll, { passive: true });
		headerEl.addEventListener("scroll", onHeaderScroll, { passive: true });
		return () => {
			bodyEl.removeEventListener("scroll", onBodyScroll);
			headerEl.removeEventListener("scroll", onHeaderScroll);
		};
	}, []);

	useEffect(() => {
		const ext = isArray(extSortBy) ? extSortBy : [];
		const local = isArray(sortBy) ? sortBy : [];
		if (!isSortEqual(local, ext)) {
			setSortBy(ext);
		}
		// eslint-disable-next-line
	}, [extSortBy, setSortBy, sortBy]);

	useEffect(() => {
		if (typeof onSortByChange !== "function") return;
		const ext = isArray(extSortBy) ? extSortBy : [];
		const local = isArray(sortBy) ? sortBy : [];
		if (!isSortEqual(local, ext)) {
			onSortByChange(local);
		}
		// eslint-disable-next-line
	}, [sortBy, extSortBy, onSortByChange]);

	const { width: viewportWidth } = useWindowSize();
	const isMobileViewport = typeof viewportWidth === "number" && viewportWidth < 768;

	const currentPage = Math.max(1, Number(extPageIndex || 1));
	const perPage = Math.max(1, Number(extPageSize || initialPageSize));
	const effectivePageSizeOptions = isMobileViewport ? [20] : pageSizeOptions;

	const canPrev = currentPage > 1;
	const canNext = totalPages ? currentPage < totalPages : false;

	const handlePrev = () => {
		if (!canPrev) return;
		onPageChange && onPageChange(currentPage - 1);
	};

	const handleNext = () => {
		if (!canNext) return;
		onPageChange && onPageChange(currentPage + 1);
	};

	const headerCols = headerGroups[0]?.headers || [];
	const renderSharedColGroup = () => (
		<colgroup>
			{headerCols.map((column) => {
				const style = {};
				if (column.minWidth != null) style.minWidth = typeof column.minWidth === "number" ? `${column.minWidth}px` : column.minWidth;
				if (column.maxWidth != null) style.maxWidth = typeof column.maxWidth === "number" ? `${column.maxWidth}px` : column.maxWidth;
				if (column.width != null) style.width = typeof column.width === "number" ? `${column.width}px` : column.width;
				return <col key={column.id} style={style} />;
			})}
		</colgroup>
	);

	const startItem = fromItem > 0 ? fromItem : totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1;
	const endItem = toItem > 0 ? toItem : totalItems === 0 ? 0 : Math.min(currentPage * perPage, totalItems);

	return (
		<div className={`${styles.wrap} ${className || ""}`} style={customStyle || {}}>
			<TableToolbar
				styles={styles}
				pageSize={perPage}
				pageSizeOptions={effectivePageSizeOptions}
				onChangePageSize={(v) => {
					onPageSizeChange && onPageSizeChange(v);
					onPageChange && onPageChange(1);
				}}
				canPreviousPage={canPrev}
				canNextPage={canNext}
				previousPage={handlePrev}
				nextPage={handleNext}
				startItem={startItem}
				endItem={endItem}
				totalItems={totalItems}
				isColumnsMenuVisible={isColumnsMenuVisible}
				setIsColumnsMenuVisible={setIsColumnsMenuVisible}
				columnsMenuRef={columnsMenuRef}
				allColumns={allColumns}
				actionButtons={actionButtons}
			/>

			<div className={styles.tableWrap}>
				{isMobileViewport ? (
					<div
						className={styles.bodyContainer}
						style={{
							height: customHeight ? customHeight : hasFilters ? "calc(100dvh - 8.5vh - 140px)" : "calc(100dvh - 8.5vh - 100px)",
						}}
						role="region"
						aria-label="Listado en formato tarjeta"
					>
						<ResponsiveCardList columns={memoizedColumns} data={memoizedData} onRowClick={onRowClick} />
					</div>
				) : (
					<>
						<div ref={headerContainerRef} className={styles.headerContainer}>
							<TableHeader
								styles={styles}
								headerTableRef={headerTableRef}
								headerGroups={headerGroups}
								renderSharedColGroup={renderSharedColGroup}
								compactHeaders={compactHeaders}
							/>
						</div>{" "}
						<div
							ref={bodyContainerRef}
							className={styles.bodyContainer}
							style={{
								height: customHeight ? customHeight : hasFilters ? "65vh" : "70vh",
							}}
							role="region"
							aria-label="Contenido de la tabla"
						>
							<TableBody
								styles={styles}
								getTableProps={getTableProps}
								getTableBodyProps={getTableBodyProps}
								page={rows}
								prepareRow={prepareRow}
								renderSharedColGroup={renderSharedColGroup}
								onRowClick={onRowClick}
								shouldEnableExpander={shouldEnableExpander}
								renderSubComponent={renderSubComponent}
								visibleColumns={visibleColumns}
								compactBody={compactBody}
								useExpandableRows={useExpandableRows}
								renderExpandedContent={renderExpandedContent}
								useRowspan={useRowspan}
								rowspanConfig={rowspanConfig}
							/>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
